import React from "react";
import tw from "twin.macro";
import type MuxPlayerElement from "@mux/mux-player";
import { Icon, type IconComponent } from "~/features/ui/icon";
import Pause from "~/public/icons/pause.svg";
import Play from "~/public/icons/play.svg";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { up } from "~/features/app/screens";

type Props = {
  videoRef?: React.RefObject<HTMLVideoElement | MuxPlayerElement | null>;
  size?: keyof typeof sizes;
  variant?: keyof typeof variants;
  icon?: IconComponent;
  [key: string]: unknown;
};

const sizes = {
  small: tw`w-40 h-40`,
  medium: tw`w-48 h-48 lg:(w-56 h-56)`,
  large: tw`w-40 h-40 lg:(w-72 h-72)`,
} as const;

const variants = {
  primary: tw`text-white-100-light bg-black-transparent-40 can-hover:hover:(bg-black-transparent-60)`,
  secondary: tw`text-black-100-light bg-white-100-light`,
  tertiary: tw`text-white-100-light bg-white-100-light/10 backdrop-blur-xl rounded-2`,
} as const;

const buttonStyle = tw`
  relative flex shrink-0 items-center justify-center transition-colors duration-250 ease-in-out
  after:(content-[""] absolute bottom-0 left-0 w-full h-2 bg-blue-100 origin-left [transform: scaleX(var(--progress))])
`;

export function VideoControls(props: Props & React.ComponentPropsWithRef<"button">) {
  const { size = "small", variant = "primary", videoRef, icon, onClick, ...rest } = props;
  const isDecorativeElement = rest.as != null && rest.as !== "button";

  const [isPlaying, setIsPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const isLg = useMediaQuery(up("lg"));

  const toggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    const video = videoRef?.current;

    if (video && typeof video.play === "function") {
      if (video.paused || video.ended) {
        video.play();
      } else {
        video.pause();
      }
    }

    if (onClick) {
      onClick(event);
    }
  };

  // The lazy-loaded <mux-player> attaches to videoRef.current after first
  // paint, but RefObjects don't trigger re-renders. Poll once via rAF until
  // the element is available, then mirror it into state so the
  // play/pause/ended/timeupdate listeners can subscribe.
  const [video, setVideo] = React.useState<HTMLVideoElement | MuxPlayerElement | null>(() => videoRef?.current ?? null);
  React.useEffect(() => {
    if (videoRef?.current) {
      setVideo(videoRef.current);
      return;
    }
    let frame = 0;
    const tick = () => {
      if (videoRef?.current) {
        setVideo(videoRef.current);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [videoRef]);

  React.useEffect(() => {
    if (!video || typeof video.addEventListener !== "function") {
      return;
    }

    setIsPlaying(!video.paused);

    function onPlay() {
      setIsPlaying(true);
    }
    function onPause() {
      setIsPlaying(false);
    }
    function onEnded() {
      setIsPlaying(false);
    }
    function onTimeUpdate() {
      setProgress(video!.currentTime / video!.duration);
    }

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);
    video.addEventListener("timeupdate", onTimeUpdate);

    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, [video]);

  return (
    <button
      css={[buttonStyle, sizes[size], variants[variant]]}
      style={{ "--progress": `${progress}` } as React.CSSProperties}
      aria-label={isDecorativeElement ? undefined : isPlaying ? "Pause video" : "Play video"}
      aria-hidden={isDecorativeElement ? true : undefined}
      onClick={toggle}
      {...rest}
    >
      <Icon icon={icon ? icon : isPlaying ? Pause : Play} size={isLg ? "16" : "12"} />
    </button>
  );
}
